import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircleDots, X, PaperPlaneRight, Sparkle, Crown, MapPin, ArrowRight, Copy, WhatsappLogo, ChatText, Check, Phone, Envelope } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";

const STORAGE_SESSION = "mc68_concierge_session";
const STORAGE_MSGS = "mc68_concierge_msgs";

/**
 * Floating AI concierge widget — bottom-left on desktop, bottom-center on mobile.
 * Positioned opposite the "Comment ça marche ?" launcher (bottom-right).
 */
export default function ConciergeWidget() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cookieOk, setCookieOk] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_MSGS);
      if (raw) setMessages(JSON.parse(raw));
      const sid = sessionStorage.getItem(STORAGE_SESSION);
      if (sid) setSessionId(sid);
    } catch (e) {
      // ignore
    }
    const check = () => setCookieOk(!!localStorage.getItem("mc68_cookies"));
    check();
    const itv = setInterval(check, 600);
    return () => clearInterval(itv);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    try { sessionStorage.setItem(STORAGE_MSGS, JSON.stringify(messages.slice(-20))); } catch (e) { console.warn("concierge session persist failed:", e); }
  }, [messages, open]);

  const welcome = lang === "en"
    ? "Hi! I'm your local concierge. What are you looking for in Haut-Rhin? (bakery, plumber, restaurant, etc.)"
    : lang === "de"
    ? "Hallo! Ich bin dein lokaler Concierge. Was suchst du im Haut-Rhin? (Bäcker, Klempner, Restaurant, usw.)"
    : "Bonjour ! Je suis le concierge local. Que cherchez-vous dans le Haut-Rhin ? (boulangerie, plombier, restaurant, etc.)";

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const next = [...messages, { id: `u_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, role: "user", content: text }];
    setMessages(next);
    setBusy(true);
    try {
      const res = await api.post("/public/concierge", {
        messages: next.map((m) => ({ role: m.role, content: m.content })),
        language: lang || "fr",
        session_id: sessionId,
      });
      const newSid = res.data.session_id;
      if (newSid && newSid !== sessionId) {
        setSessionId(newSid);
        sessionStorage.setItem(STORAGE_SESSION, newSid);
      }
      setMessages((m) => [...m, {
        id: `a_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        role: "assistant",
        content: res.data.reply || "...",
        businesses: res.data.businesses || [],
      }]);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setMessages((m) => [...m, {
        id: `a_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        role: "assistant",
        content: status === 429 ? (detail || "Trop de messages, réessayez dans une heure.") : "Désolé, je rencontre un souci. Réessayez dans un instant.",
      }]);
    }
    setBusy(false);
  };

  const quickPrompts = lang === "en"
    ? ["Best bakery in Mulhouse", "Plumber near Colmar", "Restaurant open Sunday"]
    : lang === "de"
    ? ["Beste Bäckerei in Mulhouse", "Klempner in Colmar", "Restaurant sonntags offen"]
    : ["Meilleure boulangerie à Mulhouse", "Plombier à Colmar", "Restaurant ouvert dimanche"];

  return (
    <>
      {/* Launcher — bottom-left (opposite to the onboarding guide bottom-right) */}
      {!open && cookieOk && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 left-5 sm:bottom-5 sm:left-5 z-40 bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white shadow-[0_20px_48px_-16px_rgba(200,121,90,0.6)] rounded-full pl-4 pr-5 py-3 flex items-center gap-2 text-sm font-semibold transition-all hover:-translate-y-0.5 group"
          data-testid="concierge-launcher"
          aria-label="Ouvrir le concierge IA"
        >
          <span className="w-7 h-7 rounded-full bg-white text-secondary grid place-items-center">
            <Sparkle size={14} weight="fill" />
          </span>
          {t.widgets?.concierge || "Concierge IA"}
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed z-50 bottom-4 left-4 right-4 sm:right-auto sm:bottom-5 sm:left-5 sm:w-[400px] max-h-[min(640px,85vh)] bg-card border border-border rounded-sm shadow-2xl flex flex-col overflow-hidden"
            data-testid="concierge-panel"
          >
            <div className="relative border-b border-border bg-accent/30">
              <div className="aurora opacity-40" />
              <div className="relative flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-sm bg-secondary text-secondary-foreground grid place-items-center">
                    <Sparkle size={16} weight="fill" />
                  </div>
                  <div>
                    <div className="font-display text-base font-semibold leading-tight">Concierge Mon Commerce 68</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">IA · Haut-Rhin</div>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="p-2 rounded-sm hover:bg-muted transition-colors" data-testid="concierge-close" aria-label="Fermer">
                  <X size={16} weight="bold" />
                </button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="concierge-messages">
              {messages.length === 0 ? (
                <>
                  <div className="bg-accent/40 border border-border rounded-sm p-3 text-sm leading-relaxed" data-testid="concierge-welcome">
                    {welcome}
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {quickPrompts.map((p, i) => (
                      <button
                        key={p}
                        onClick={() => setInput(p)}
                        className="px-2.5 py-1.5 text-xs font-semibold border border-border bg-background hover:border-primary rounded-sm transition-colors"
                        data-testid={`concierge-quick-${i}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                messages.map((m, i) => (
                  <div key={m.id || `msg-${i}`} className={m.role === "user" ? "flex justify-end" : ""}>
                    <div className={m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-sm px-3 py-2 text-sm max-w-[80%]"
                      : "bg-accent/40 border border-border rounded-sm px-3 py-2 text-sm max-w-[90%] leading-relaxed"}
                      data-testid={m.role === "user" ? `concierge-msg-user-${i}` : `concierge-msg-bot-${i}`}>
                      {m.content}
                    </div>
                    {m.role === "assistant" && m.businesses && m.businesses.length > 0 && (
                      <div className="mt-2 space-y-2 w-full">
                        {m.businesses.slice(0, 5).map((b) => (
                          <ConciergeBusinessCard
                            key={b.id || b.slug}
                            biz={b}
                            onNavigate={() => setOpen(false)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
              {busy && (
                <div className="bg-accent/40 border border-border rounded-sm px-3 py-2 text-sm inline-flex items-center gap-2" data-testid="concierge-busy">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse" style={{ animationDelay: "150ms" }} />
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse" style={{ animationDelay: "300ms" }} />
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-border bg-background px-3 py-2.5 flex items-center gap-2"
              data-testid="concierge-form"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={lang === "en" ? "Ask the concierge..." : lang === "de" ? "Concierge fragen..." : "Demandez au concierge…"}
                maxLength={500}
                disabled={busy}
                className="flex-1 min-w-0 px-3 py-2 text-sm bg-background outline-none placeholder:text-muted-foreground/70 disabled:opacity-60"
                data-testid="concierge-input"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="bg-primary text-primary-foreground hover:bg-[#0f3d29] p-2.5 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="concierge-send"
                aria-label="Envoyer"
              >
                <PaperPlaneRight size={14} weight="fill" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}


/**
 * Card business dans la réponse du concierge avec actions de partage
 * (Copier / WhatsApp / SMS). Viralité locale : le visiteur transmet
 * la fiche d'un plombier ou d'un restaurant à un proche en 1 clic.
 */
function ConciergeBusinessCard({ biz, onNavigate }) {
  const [copied, setCopied] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [callbackPhone, setCallbackPhone] = useState("");
  const [callbackName, setCallbackName] = useState("");
  const [callbackMessage, setCallbackMessage] = useState("");
  const [callbackSending, setCallbackSending] = useState(false);
  const isPremium = biz.premium?.tier === "premium" || biz.premium?.tier === "premium_ai";
  const origin = typeof window !== "undefined" ? window.location.origin : "https://moncommerce68.fr";
  const url = `${origin}/entreprise/${biz.slug}`;

  const sendCallback = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const phone = callbackPhone.trim();
    const name = callbackName.trim();
    if (!phone || !name) {
      toast.error("Nom et téléphone requis");
      return;
    }
    setCallbackSending(true);
    try {
      await api.post(`/public/contact-intent/${biz.slug}`, {
        query: `[Rappel demandé via concierge IA] ${name} · ${phone}` + (callbackMessage ? ` · ${callbackMessage.slice(0, 180)}` : ""),
      });
      toast.success(`${biz.name} sera notifié — vous serez rappelé rapidement`);
      setCallbackOpen(false);
      setCallbackPhone(""); setCallbackName(""); setCallbackMessage("");
    } catch (_e) {
      toast.error("Envoi impossible — réessayez dans un instant");
    } finally {
      setCallbackSending(false);
    }
  };

  // Compose a friendly share message
  const buildShareText = () => {
    const lines = [];
    lines.push(`${biz.name}`);
    if (biz.category) lines.push(biz.category);
    const addr = [biz.address, biz.postal_code, biz.city].filter(Boolean).join(" ");
    if (addr) lines.push(addr);
    if (biz.phone) lines.push(`📞 ${biz.phone}`);
    lines.push(url);
    lines.push("(via Mon Commerce 68)");
    return lines.join("\n");
  };

  const logShare = (channel) => {
    // Fire-and-forget — ne bloque jamais le partage si l'API est lente/indispo
    api.post(`/public/share/${biz.slug}`, { channel }).catch(() => {});
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = buildShareText();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopied(true);
      toast.success("Copié dans le presse-papier");
      logShare("copy");
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      toast.error("Copie impossible — sélectionnez manuellement");
    }
  };

  const handleWhatsapp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = encodeURIComponent(buildShareText());
    logShare("whatsapp");
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const handleSms = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = encodeURIComponent(buildShareText());
    logShare("sms");
    // iOS uses `&body`, Android `?body`. Use `?body` — iOS Safari 14+ accepts both.
    window.location.href = `sms:?body=${text}`;
  };

  return (
    <div
      className="bg-background border border-border rounded-sm p-3 hover:border-primary transition-colors"
      data-testid={`concierge-biz-${biz.slug}`}
    >
      <Link
        to={`/entreprise/${biz.slug}`}
        onClick={onNavigate}
        className="block"
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="font-display text-sm font-semibold leading-tight">{biz.name}</div>
          {isPremium && (
            <span className="inline-flex items-center gap-1 bg-primary text-primary-foreground text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm">
              <Crown size={9} weight="fill" />
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
          <MapPin size={11} /> {biz.city}
          {biz.category && <> · {biz.category}</>}
        </div>
        {biz.rating_count > 0 && (
          <div className="text-xs text-secondary font-semibold">
            ★ {biz.rating_avg?.toFixed(1)} <span className="text-muted-foreground font-normal">· {biz.rating_count} avis</span>
          </div>
        )}
        <div className="text-[10px] text-primary font-bold inline-flex items-center gap-1 mt-1.5">
          Voir la fiche <ArrowRight size={10} />
        </div>
      </Link>

      {/* VAGUE 37B — Direct actions: call + request callback */}
      <div className="mt-2.5 pt-2 border-t border-border/60 flex items-center gap-1.5 flex-wrap" data-testid={`concierge-actions-${biz.slug}`}>
        {biz.phone && (
          <a
            href={`tel:${biz.phone}`}
            onClick={(e) => { e.stopPropagation(); api.post(`/public/click/${biz.slug}/phone`).catch(() => {}); }}
            className="inline-flex items-center gap-1 text-[11px] font-bold border border-primary bg-primary text-primary-foreground hover:bg-[#0f3d29] px-2.5 py-1.5 rounded-sm transition-colors"
            data-testid={`concierge-call-${biz.slug}`}
          >
            <Phone size={12} weight="fill" />
            Appeler
          </a>
        )}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCallbackOpen(true); }}
          className="inline-flex items-center gap-1 text-[11px] font-semibold border border-border bg-background hover:border-primary hover:text-primary px-2.5 py-1.5 rounded-sm transition-colors"
          data-testid={`concierge-callback-${biz.slug}`}
        >
          <Envelope size={12} weight="regular" />
          Me faire rappeler
        </button>
      </div>

      {/* Callback mini-modal */}
      {callbackOpen && (
        <div
          className="mt-3 pt-3 border-t border-primary/20 bg-primary/5 -mx-3 -mb-3 px-3 pb-3 rounded-b-sm"
          onClick={(e) => e.stopPropagation()}
          data-testid={`concierge-callback-form-${biz.slug}`}
        >
          <div className="text-[11px] uppercase tracking-widest font-bold text-primary mb-2">
            {biz.name} vous rappellera
          </div>
          <form onSubmit={sendCallback} className="space-y-2">
            <input
              type="text"
              value={callbackName}
              onChange={(e) => setCallbackName(e.target.value)}
              placeholder="Votre prénom *"
              className="w-full border border-border rounded-sm px-2.5 py-1.5 text-sm bg-background focus:border-primary outline-none"
              data-testid={`callback-name-${biz.slug}`}
              required
            />
            <input
              type="tel"
              value={callbackPhone}
              onChange={(e) => setCallbackPhone(e.target.value)}
              placeholder="Votre téléphone *"
              className="w-full border border-border rounded-sm px-2.5 py-1.5 text-sm bg-background focus:border-primary outline-none"
              data-testid={`callback-phone-${biz.slug}`}
              required
            />
            <input
              type="text"
              value={callbackMessage}
              onChange={(e) => setCallbackMessage(e.target.value)}
              placeholder="Votre demande (facultatif)"
              className="w-full border border-border rounded-sm px-2.5 py-1.5 text-xs bg-background focus:border-primary outline-none"
              data-testid={`callback-msg-${biz.slug}`}
              maxLength={180}
            />
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={callbackSending}
                className="flex-1 text-xs font-bold bg-primary text-primary-foreground hover:bg-[#0f3d29] disabled:opacity-50 px-3 py-1.5 rounded-sm transition-colors"
                data-testid={`callback-submit-${biz.slug}`}
              >
                {callbackSending ? "Envoi…" : "Envoyer"}
              </button>
              <button
                type="button"
                onClick={() => setCallbackOpen(false)}
                className="text-xs text-muted-foreground hover:text-foreground px-2"
                data-testid={`callback-cancel-${biz.slug}`}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Share actions */}
      <div className="mt-2.5 pt-2 border-t border-border/60 flex items-center gap-1.5" data-testid={`concierge-share-${biz.slug}`}>
        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mr-0.5">Partager</span>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 text-[10px] font-semibold border border-border bg-background hover:border-primary hover:text-primary px-2 py-1 rounded-sm transition-colors"
          aria-label="Copier les coordonnées"
          data-testid={`concierge-share-copy-${biz.slug}`}
        >
          {copied ? <Check size={11} weight="bold" /> : <Copy size={11} weight="regular" />}
          {copied ? "Copié" : "Copier"}
        </button>
        <button
          type="button"
          onClick={handleWhatsapp}
          className="inline-flex items-center gap-1 text-[10px] font-semibold border border-border bg-background hover:border-[#25D366] hover:text-[#25D366] px-2 py-1 rounded-sm transition-colors"
          aria-label="Partager sur WhatsApp"
          data-testid={`concierge-share-whatsapp-${biz.slug}`}
        >
          <WhatsappLogo size={11} weight="fill" />
          WhatsApp
        </button>
        <button
          type="button"
          onClick={handleSms}
          className="inline-flex items-center gap-1 text-[10px] font-semibold border border-border bg-background hover:border-primary hover:text-primary px-2 py-1 rounded-sm transition-colors"
          aria-label="Envoyer par SMS"
          data-testid={`concierge-share-sms-${biz.slug}`}
        >
          <ChatText size={11} weight="regular" />
          SMS
        </button>
      </div>
    </div>
  );
}
